/**
 * As dicas da faixa: uma frase, e onde clicar para fazer o que ela diz.
 *
 * ── Por que cada dica carrega um ALVO, e não um texto de ajuda ────────────
 *
 * Dica que só explica é documentação, e documentação numa faixa é ruído com
 * um X. O que a faz valer é o segundo passo: a frase diz o que dá para fazer,
 * e o clique acende a borda exatamente onde fazer. Sem o alvo, ela vira uma
 * promessa sem endereço.
 *
 * O alvo é o id de um elemento REAL da tela. `tutorialAlvos.test.ts` guarda
 * que ele existe — o mesmo teste que já cuida dos passos do tour, porque o
 * modo de falhar é idêntico: apontar para o vazio não dá erro, e ninguém
 * descobre até tentar.
 *
 * ── Uma dica sem alvo é legítima ──────────────────────────────────────────
 *
 * O resumo mensal por email é automático: não há tela, não há botão, não há
 * o que clicar. `alvo: null` é a resposta honesta. Inventar um destino para
 * ela — "vá em ajustes" — mandaria a pessoa procurar uma configuração que
 * não existe, que é o erro que a lista de features do WhatsApp existe para
 * não cometer.
 *
 * ── O ciclo ───────────────────────────────────────────────────────────────
 *
 * Vista uma, ela sai da fila. Quando a fila esvazia, ela recomeça — a faixa
 * **não se aposenta**, foi decisão explícita. Então a regra não é "acabou,
 * some", é "acabou, volta ao começo".
 *
 * Módulo puro: não toca no DOM nem no `localStorage`. Quem guarda é a faixa.
 */

export interface Dica {
  /** Estável e curto: é a chave do "já vi", e vive no navegador dela. */
  id: string;
  texto: string;
  /** Id do elemento a destacar. `null` quando não há para onde ir. */
  alvo: string | null;
}

export const DICAS: Dica[] = [
  { id: 'colar-print', alvo: 'tf-paste-btn-wrap',
    texto: '🚀 Dica rápida: cole o print da sua transação e a gente preenche tudo. Você só confere e salva.' },
  { id: 'foto-galeria', alvo: 'tf-scan-btn-wrap',
    texto: '✨ Facilite sua vida: escolha um comprovante da sua galeria e deixe a gente transformar a imagem em uma transação.' },
  { id: 'foto-lista', alvo: 'tf-scan-btn-wrap',
    texto: '🧾 Fez várias compras? Tire foto da lista ou do cupom e cada linha vira um lançamento, já com a categoria sugerida.' },
  // No card de Categorias, não no formulário de transação: é lá que o "+"
  // cria categoria com cor. O formulário também cria, mas no meio de um
  // lançamento — e a dica não fala de lançar nada (pedido dela em 15/09).
  { id: 'categoria-cor', alvo: 'sec-chart',
    texto: '🎨 Deixe tudo com a sua cara: crie categorias com cores personalizadas e entenda rapidinho pra onde seu dinheiro está indo.' },
  { id: 'tendencia-categoria', alvo: 'sec-chart',
    texto: '📊 Olha isso: acompanhe a tendência dos seus gastos por categoria nos últimos 6 meses e descubra seus hábitos.' },
  { id: 'parcelamento', alvo: 'sec-form',
    texto: '💳 Menos trabalho pra você: cadastre um parcelamento uma única vez e a gente lança todas as próximas parcelas automaticamente.' },
  { id: 'recorrente', alvo: 'sec-form',
    texto: '🔁 Automatize sua rotina: registre um gasto recorrente uma vez e nós cuidamos dos próximos meses.' },
  { id: 'limite-metodo', alvo: 'sec-methods',
    texto: '💰 Fique no controle: defina um limite para cada método de pagamento e receba um aviso antes de passar dele.' },
  { id: 'dia-da-fatura', alvo: 'sec-methods',
    texto: '🗓️ Tem cartão de crédito? Informe o dia de fechamento da fatura e veja automaticamente quais compras entram no próximo ciclo.' },
  { id: 'vantagens-do-cartao', alvo: 'sec-methods',
    texto: '💎 Seu cartão tem vantagens que você nem usa: escolha a bandeira e o nível no método de pagamento e veja a lista.' },
  { id: 'pontos-e-milhas', alvo: 'sec-methods',
    texto: '✈️ Junta pontos no cartão? Informe o saldo e o Finn calcula quanto eles valem em reais e para qual programa vale transferir.' },
  { id: 'busca-em-linguagem', alvo: 'sec-transactions',
    texto: '🔎 Pesquise como você fala: experimente escrever "quanto gastei com mercado este mês?" e a gente encontra a resposta.' },
  { id: 'exportar-planilha', alvo: 'sec-transactions',
    texto: '📤 Precisa dos números fora daqui? Filtre a lista de transações e exporte tudo como planilha, com o total no fim.' },
  { id: 'lembrete', alvo: 'sec-reminders',
    texto: '⏰ Não esqueça mais nada: escreva um lembrete e receba avisos por WhatsApp e e-mail.' },
  { id: 'lembrete-data-final', alvo: 'sec-reminders',
    texto: '📅 Quer insistir até lembrar? Defina uma data final e continuamos enviando lembretes até lá.' },
  { id: 'lembrete-vinculado', alvo: 'sec-reminders',
    texto: '💸 Organização no automático: vincule um lembrete a uma parcela e, quando ela for paga ou recebida, atualizamos tudo para você.' },
  { id: 'investimentos', alvo: 'sec-investments',
    texto: '📈 Faça seu dinheiro trabalhar: cadastre seus investimentos e acompanhe uma projeção de rendimento para os próximos meses.' },
  { id: 'metas', alvo: 'sec-goals',
    texto: '🎯 Transforme planos em realidade: crie metas financeiras e acompanhe sua evolução até alcançá-las.' },
  { id: 'meta-compartilhada', alvo: 'sec-goals',
    texto: '👥 Juntando dinheiro com alguém? Crie uma meta compartilhada, convide a pessoa e acompanhem juntos quanto falta.' },
  { id: 'limite-de-gastos', alvo: 'sec-goals',
    texto: '🚦 Evite surpresas: defina um limite de gastos e receba um aviso antes de comprometer seu orçamento.' },
  { id: 'dividas', alvo: 'sec-debts',
    texto: '🏦 Enxergue suas dívidas com clareza: acompanhe tudo em um só lugar e tenha uma visão melhor da sua evolução financeira.' },
  // Sem alvo: o resumo mensal é automático e não tem tela para apontar.
  { id: 'resumo-mensal', alvo: null,
    texto: '📬 Todo mês tem novidade: receba um resumo da sua vida financeira com insights para gastar melhor.' },
  // A assinatura fica no menu do avatar, como o WhatsApp.
  { id: 'newsletter', alvo: 'nav-avatar-btn',
    texto: '📰 Comece o dia por dentro: assine a newsletter dos dias úteis, com a leitura dos seus gastos e as notícias dos temas que você escolher.' },
  /*
   * Logo depois do resumo mensal, porque fala do MESMO email.
   *
   * ── A frase diz que a outra pessoa decide, e isso não é enfeite ────────
   *
   * O erro que este recurso convida é achar que cadastrar um email já envia.
   * Uma dica que dissesse só "compartilhe o seu relatório" plantaria
   * exatamente essa leitura, e ela só se desfaria um mês depois — quando o
   * relatório não chegasse para quem ela achou que já recebia.
   *
   * ── Por que aqui sim, se o bot do WhatsApp não oferece ─────────────────
   *
   * `ROTAS_SEM_FEATURE` no backend deixa este recurso FORA das ofertas
   * automáticas do bot, e a razão continua de pé: uma sugestão que chega numa
   * conversa, sem ser pedida, empurrando alguém a revelar as próprias
   * finanças a terceiros, é coisa que um app não deve fazer sozinho.
   *
   * A faixa é outra situação. Ela não vai atrás de ninguém: aparece dentro do
   * app, para quem já está olhando as próprias contas, e sai com um X. A
   * diferença é entre estar num catálogo e tocar a campainha.
   */
  /*
   * O dinheiro da casa, logo depois do compartilhamento — os dois falam de
   * abrir dinheiro para outra pessoa, e a diferença entre eles precisa ficar
   * clara no lugar onde ela vai escolher errado.
   *
   * O compartilhamento manda um RELATÓRIO por email, uma vez por mês, para
   * quem pode nem usar o app. A casa é uma TELA, contínua, e só para quem tem
   * conta. A frase diz "quem mora com você" justamente para não ser lida como
   * a outra coisa.
   *
   * ── Por que a faixa oferece o que o bot não oferece ────────────────────
   *
   * `ROTAS_SEM_FEATURE` deixa `/api/grupo` e `/api/casa` fora das ofertas
   * automáticas do WhatsApp, e a razão continua de pé: propor "chame seu
   * parceiro para ver o dinheiro da casa" numa conversa não pedida é opinar
   * sobre a relação de quem recebe, e pode chegar no pior momento possível.
   *
   * A faixa não vai atrás de ninguém. Ela aparece dentro do app, para quem já
   * está olhando as próprias contas, e sai com um X.
   */
  { id: 'dinheiro-da-casa', alvo: 'sec-casa',
    texto: '🏠 Divide as contas com alguém? Marque o que é da casa e vejam juntos para onde o dinheiro foi. O que for pessoal continua só seu.' },
  { id: 'compartilhar-relatorio', alvo: 'nav-avatar-btn',
    texto: '🤝 Divida com quem cuida junto: autorize alguém a receber o seu relatório mensal. A pessoa recebe um convite e decide se aceita.' },
  // O WhatsApp abre pelo menu do avatar — é para lá que a borda aponta.
  { id: 'whatsapp-lancar', alvo: 'nav-avatar-btn',
    texto: '💬 Prefere WhatsApp? Lance suas despesas sem precisar abrir o app.' },
  { id: 'whatsapp-foto', alvo: 'nav-avatar-btn',
    texto: '📷 Um comprovante vale mais que mil cliques: envie uma foto pelo WhatsApp e a gente cria a transação para você.' },
  // O conector de IA também abre pelo menu do avatar, como o WhatsApp.
  { id: 'conector-ia', alvo: 'nav-avatar-btn',
    texto: '🤖 Converse sobre o seu dinheiro no Claude ou no ChatGPT: conecte e pergunte coisas que só quem vê seus números responde.' },
  // Não há botão de sugestão: ela nasce quando alguém pede no WhatsApp algo
  // que o app não faz. O WhatsApp abre pelo menu do avatar.
  { id: 'sugestoes', alvo: 'nav-avatar-btn',
    texto: '💡 Sentiu falta de algo? Peça pelo WhatsApp: o que o app ainda não faz vira sugestão para as próximas novidades.' },
  { id: 'vale-a-pena', alvo: 'ac-ancora',
    texto: '🤔 Na dúvida, pergunte ao Finn: quer saber se vale a pena fazer uma compra? Ele ajuda você a decidir.' },
  { id: 'quanto-posso-gastar', alvo: 'ac-ancora',
    texto: '🛍️ Antes de comprar, confira: pergunte ao Finn quanto ainda pode gastar este mês e compre com mais tranquilidade.' },
  { id: 'tema', alvo: 'nav-theme-picker',
    texto: '✨ Deixe com a sua cara: escolha a cor do tema que mais combina com você.' },
];

/**
 * A fila do momento.
 *
 * `disponiveis` são os ids das dicas que PODEM aparecer agora — quem chama
 * filtra pelas que têm alvo na tela. No celular o formulário completo não é
 * montado, então `tf-paste-btn-wrap` não existe, e oferecer "cole o print"
 * ali acenderia o vazio.
 *
 * Quando todas as disponíveis já foram vistas, a fila recomeça inteira. É o
 * ciclo: a faixa não se aposenta.
 */
export const filaDeDicas = (vistas: string[], disponiveis: string[]): Dica[] => {
  const candidatas = DICAS.filter(d => disponiveis.includes(d.id));
  const naoVistas = candidatas.filter(d => !vistas.includes(d.id));
  return naoVistas.length > 0 ? naoVistas : candidatas;
};

export const proximaDica = (vistas: string[], disponiveis: string[]): Dica | null =>
  filaDeDicas(vistas, disponiveis)[0] ?? null;

/**
 * Guarda que ela viu, e fecha o ciclo quando for a hora.
 *
 * O reinício olha só as DISPONÍVEIS, não as 23. Se ela usar sempre o celular,
 * as dicas do formulário completo nunca aparecem — e esperar a lista chegar a
 * 23 deixaria a fila travada para sempre na última disponível, repetindo.
 */
export const registrarVista = (
  vistas: string[], id: string, disponiveis: string[],
): string[] => {
  const nova = vistas.includes(id) ? vistas : [...vistas, id];
  const candidatas = DICAS.filter(d => disponiveis.includes(d.id)).map(d => d.id);
  return candidatas.every(c => nova.includes(c)) ? [] : nova;
};
